import { Box, Checkbox, Typography } from "@mui/joy";

import { Button } from "../../../../shared";

export type RoleOption = {
    Id: string;
    Name: string;
    Description?: string | null;
};

type RoleSelectionListProps = {
    roles: RoleOption[];
    selectedRoleIds: string[];
    onChange: (roleIds: string[]) => void;
    loading?: boolean;
    disabled?: boolean;
};

export default function RoleSelectionList({
    roles,
    selectedRoleIds,
    onChange,
    loading = false,
    disabled = false,
}: RoleSelectionListProps) {
    const selectedCount = selectedRoleIds.length;
    const allSelected = roles.length > 0 && selectedCount === roles.length;

    const toggleRole = (roleId: string) => {
        if (disabled) return;
        if (selectedRoleIds.includes(roleId)) {
            onChange(selectedRoleIds.filter((id) => id !== roleId));
            return;
        }
        onChange([...selectedRoleIds, roleId]);
    };

    const handleSelectAll = () => {
        if (disabled) return;
        onChange(allSelected ? [] : roles.map((role) => role.Id));
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1}>
                <Box>
                    <Typography level="title-sm">Roles</Typography>
                    <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.25 }}>
                        {selectedCount} of {roles.length} selected
                    </Typography>
                </Box>
                {!disabled ? (
                    <Button
                        type="button"
                        variant="plain"
                        color="neutral"
                        size="sm"
                        onClick={handleSelectAll}
                        disabled={loading || !roles.length}
                        sx={{ borderRadius: "4px", flexShrink: 0 }}
                    >
                        {allSelected ? "Clear all" : "Select all"}
                    </Button>
                ) : null}
            </Box>

            {loading ? (
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    Loading roles…
                </Typography>
            ) : !roles.length ? (
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                    No roles are configured yet. Create roles under Admin → Roles first.
                </Typography>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                    columnGap={2}
                    rowGap={0.25}
                >
                    {roles.map((role) => {
                        const checked = selectedRoleIds.includes(role.Id);
                        return (
                            <Box
                                key={role.Id}
                                component="label"
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    py: 0.75,
                                    cursor: disabled ? "default" : "pointer",
                                    opacity: disabled ? 0.85 : 1,
                                }}
                            >
                                <Checkbox
                                    size="sm"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => toggleRole(role.Id)}
                                    sx={{ mt: 0.15 }}
                                />
                                <Box minWidth={0}>
                                    <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                        {role.Name}
                                    </Typography>
                                    {role.Description ? (
                                        <Typography
                                            level="body-xs"
                                            sx={{ color: "text.tertiary", mt: 0.15 }}
                                        >
                                            {role.Description}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
